/**
 * ADR 0513 D13 — ค่าตั้งของโหมดรอแอปคนขับ · **ต้นฉบับ**
 * plan: 4-Archives/tms/deeptrack-plan/0513-driver-standby-gps/0513-driver-standby-gps.md
 *
 * ค่ามาจาก server (`tenant.settings.driver_standby`) แต่ผู้อ่านทุกฝั่งต้องผ่าน `clampStandbyConfig` ก่อนใช้:
 * ค่าผิดชนิด / ขาด → ค่าเริ่ม · นอกกรอบ → ขอบ · คีย์ที่ไม่รู้จัก → คืนใน `unknownKeys` ให้ผู้อ่าน log warn
 *
 * API โหลดแพ็กเกจนี้ตอนรันไม่ได้ (ADR 0150) → สำเนาที่ `apps/api/src/modules/driver/standby/standby-config.ts`
 * native ไม่พึ่งแพ็กเกจนี้ → สำเนาที่ `apps/driver-app/src/standby/config.ts`
 * ทั้งสามฝั่งปักกับ `driver-standby-config.cases.json` ด้วยเทสของตัวเอง ⇒ ดริฟต์แล้วแดง
 *
 * ทุกที่ที่ค่านี้มีผล (อัปรายการนี้เมื่อเพิ่มที่ใช้):
 * - api   `standby.controller.ts` → คำตอบ `POST /driver/standby/token` และ `/poll` · สคริปต์ `scripts/standby-config.ts --set`
 * - app   `apps/driver-app/src/standby/controller.ts` → จังหวะถาม, profile standby, timeout, ข้อความแจ้งเตือน
 * - web   `apps/driver/src/features/standby/` (อ่านเพื่อแสดงผลเท่านั้น)
 */

export const STANDBY_CONFIG_KEYS = [
  'enabled',
  'poll_sec',
  'capture_sec',
  'stale_hours',
  'token_ttl_days',
  'null_streak_to_stop',
  'request_timeout_sec',
  'notification_text',
  'min_app_build',
] as const;
export type StandbyConfigKey = (typeof STANDBY_CONFIG_KEYS)[number];

export interface StandbyConfig {
  /** สวิตช์ฉุกเฉินรายองค์กร (D15 ชั้น 1) */
  enabled: boolean;
  /** ถามหางานทุกกี่วินาที (Q5) */
  poll_sec: number;
  /** จังหวะพิกัดตอนรอ (D4) */
  capture_sec: number;
  /** ข้ามงานที่เริ่มนานกว่านี้ (Q2) */
  stale_hours: number;
  /** อายุบัตรโหมดรอ (Q3) */
  token_ttl_days: number;
  /** ไม่เจองานกี่รอบติดจึงกลับไปรอ (D5) */
  null_streak_to_stop: number;
  /** เวลาหมดรอของทุกคำขอในเส้นโหมดรอ (D14) */
  request_timeout_sec: number;
  /** ข้อความแจ้งเตือน FGS (D11) */
  notification_text: string;
  /** build ขั้นต่ำของแอปที่ยอมให้เปิดโหมดรอ (D15 ชั้น 1) · 0 = ทุก build */
  min_app_build: number;
}

export const STANDBY_NOTIFICATION_MAX = 80;

export const STANDBY_CONFIG_DEFAULTS: Readonly<StandbyConfig> = Object.freeze({
  enabled: true,
  poll_sec: 180,
  capture_sec: 10,
  stale_hours: 24,
  token_ttl_days: 7,
  null_streak_to_stop: 2,
  request_timeout_sec: 20,
  notification_text: 'DeepTrack พร้อมรับงาน บันทึกเส้นทางเมื่องานเริ่ม',
  min_app_build: 0,
});

type NumericKey = Exclude<StandbyConfigKey, 'enabled' | 'notification_text'>;

/** [ต่ำสุด, สูงสุด] ของค่าตัวเลข — นอกกรอบให้ใช้ขอบ (ADR 0513 D13) */
export const STANDBY_CONFIG_BOUNDS: Readonly<Record<NumericKey, readonly [number, number]>> = Object.freeze({
  poll_sec: [60, 900],
  capture_sec: [5, 60],
  stale_hours: [1, 72],
  token_ttl_days: [1, 30],
  null_streak_to_stop: [1, 5],
  request_timeout_sec: [5, 60],
  min_app_build: [0, 1_000_000_000],
});

export interface ClampedStandbyConfig {
  config: StandbyConfig;
  /** คีย์ที่ server ส่งมาแต่ไม่อยู่ใน `STANDBY_CONFIG_KEYS` — ผู้อ่าน log warn แล้วข้าม */
  unknownKeys: string[];
}

function clampNumber(v: unknown, key: NumericKey): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return STANDBY_CONFIG_DEFAULTS[key];
  const [lo, hi] = STANDBY_CONFIG_BOUNDS[key];
  return Math.min(hi, Math.max(lo, Math.round(v)));
}

export function clampStandbyConfig(raw: unknown): ClampedStandbyConfig {
  const src = raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const known = new Set<string>(STANDBY_CONFIG_KEYS);
  const unknownKeys = Object.keys(src).filter((k) => !known.has(k)).sort();

  const text = typeof src.notification_text === 'string' ? src.notification_text.trim() : '';
  const config: StandbyConfig = {
    enabled: typeof src.enabled === 'boolean' ? src.enabled : STANDBY_CONFIG_DEFAULTS.enabled,
    poll_sec: clampNumber(src.poll_sec, 'poll_sec'),
    capture_sec: clampNumber(src.capture_sec, 'capture_sec'),
    stale_hours: clampNumber(src.stale_hours, 'stale_hours'),
    token_ttl_days: clampNumber(src.token_ttl_days, 'token_ttl_days'),
    null_streak_to_stop: clampNumber(src.null_streak_to_stop, 'null_streak_to_stop'),
    request_timeout_sec: clampNumber(src.request_timeout_sec, 'request_timeout_sec'),
    notification_text:
      text.length > 0 && text.length <= STANDBY_NOTIFICATION_MAX ? text : STANDBY_CONFIG_DEFAULTS.notification_text,
    min_app_build: clampNumber(src.min_app_build, 'min_app_build'),
  };
  return { config, unknownKeys };
}
